import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { confirmationDialogOption, confirmationDialogOptions } from "@common-views/calendar/calendar-view";
import { handleAddScheduledMessageCloseThunk, handleRecurringMessageEditThunk } from "@common/thunks/UserMessagesThunks";
import React, { useEffect } from "react";
import { MessageConfirmationDialog } from "./message-confirmation-dialog";
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';

const EditRecurringDialogConfirmation: React.FC = () => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const showConfirmationDialog = useAppSelector(state => state.AddScheduledMessageButtonReducer.showConfirmationDialog);
  const openedFromContext = useAppSelector(state => state.AddScheduledMessageButtonReducer.openedFromContext);

  // Auto-execute with "all messages" scope when opened from calendar sidebar
  useEffect(() => {
    if (showConfirmationDialog && openedFromContext === 'calendar-sidebar') {
      // Automatically use "all messages" scope and execute without showing dialog
      dispatch(handleRecurringMessageEditThunk({ scope: 'all' }));
      dispatch(handleAddScheduledMessageCloseThunk());
    }
  }, [showConfirmationDialog, openedFromContext, dispatch]);

  const onEditRecurringDialogClose = (selection?: confirmationDialogOption) => {
    // If no selection was made, just close the dialog
    if (!selection) {
      dispatch(handleAddScheduledMessageCloseThunk());
      return;
    }

    // Map the selection to a scope value
    const scopeMap = {
      "All messages": "all",
      "This message": "this",
      "This and following messages": "thisAndFollowing"
    } as const;
    
    const scope = (scopeMap[selection] ?? 'all') as "all" | "this" | "thisAndFollowing";
    
    // Dispatch the edit action and close
    dispatch(handleRecurringMessageEditThunk({ scope }));
    dispatch(handleAddScheduledMessageCloseThunk());
  };

  // Don't show dialog if opened from calendar sidebar (auto-execution handled above)
  if (!showConfirmationDialog || openedFromContext === 'calendar-sidebar') {
    return null;
  }

  return (
    <MessageConfirmationDialog
      title={t('scheduledMessage.confirmation.editRecurring')}
      onClose={onEditRecurringDialogClose}
      options={confirmationDialogOptions}
    />
  );
};

export default EditRecurringDialogConfirmation; 